/**
 * Spec §7 — which entity the portal is showing. Remembered per browser, keyed by the OWNER's ClientRef so a
 * second person signing in on the same browser (a spouse) never inherits the first person's choice.
 * The server decides access on every request; this is only the request, never the permission.
 */
export const ACTIVE_ENTITY_HEADER = 'X-Active-Client-Ref';
const STORAGE_KEY = 'babomrs.clientPortal.activeEntity';

/**
 * The RFC 7807 `type` the API puts on a 403 that refuses the requested entity
 * (`PortalEntityAccessDeniedException.ProblemType`). Only this 403 means "the remembered entity is no longer
 * yours to view"; any other 403 is left to the page that received it.
 */
export const PORTAL_ENTITY_REFUSED_TYPE = 'https://docs.babomrs.com/errors/portal-entity-refused';

interface Stored { owner: string; clientRef: string }

let owner: string | null = null;
let active: string | null = null;

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<Stored>;
    return typeof value.owner === 'string' && typeof value.clientRef === 'string'
      ? { owner: value.owner, clientRef: value.clientRef }
      : null;
  } catch {
    return null;
  }
}

function write(value: Stored | null): void {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private window, blocked site data): the choice lasts for this page load only.
  }
}

/** Once /client/me resolves: restore this person's remembered choice, or their own record. */
export function initActiveEntity(ownClientRef: string): string {
  owner = ownClientRef;
  const stored = read();
  active = stored && stored.owner === ownClientRef ? stored.clientRef : ownClientRef;
  return active;
}

export function setActiveEntity(clientRef: string): void {
  if (!owner) return;
  active = clientRef;
  write(clientRef === owner ? null : { owner, clientRef });
}

export function getActiveEntity(): string | null {
  return active;
}

/** The header every API call carries — nothing at all for the caller's own record. */
export function activeEntityHeaders(): Record<string, string> {
  return owner && active && active !== owner ? { [ACTIVE_ENTITY_HEADER]: active } : {};
}

/** Test helper and sign-out: forget the in-memory choice (storage is left as the owner last set it). */
export function resetActiveEntity(): void {
  owner = null;
  active = null;
}

/** True when `error` (what `apiClient` throws: the parsed problem body) is the API refusing the entity. */
export function isEntityRefused(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const body = error as { status?: unknown; type?: unknown };
  return body.status === 403 && body.type === PORTAL_ENTITY_REFUSED_TYPE;
}
